import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmModal } from './ConfirmModal';
import { changeRoleSchema, type ChangeRoleInput } from '../schemas';
import { useChangeRole } from '../hooks';

interface Props {
  open: boolean;
  userId: string;
  currentRole: string;
  userName?: string;
  onClose: () => void;
}

export function RoleManagementModal({ open, userId, currentRole, userName, onClose }: Props) {
  const mutation = useChangeRole();
  const form = useForm<ChangeRoleInput>({
    resolver: zodResolver(changeRoleSchema),
    defaultValues: { role: currentRole as ChangeRoleInput['role'] },
  });

  const submit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync({ id: userId, role: values.role });
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title={`Change role for ${userName ?? 'user'}`}
      confirmLabel="Save"
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit()}
    >
      <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
        New role
      </label>
      <select
        {...form.register('role')}
        className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
      >
        <option value="USER">USER</option>
        <option value="EDITOR">EDITOR</option>
        <option value="ADMIN">ADMIN</option>
        <option value="SUPER_ADMIN">SUPER_ADMIN</option>
      </select>
      {mutation.error && (
        <p className="text-xs text-red-300 mt-2">{(mutation.error as Error).message}</p>
      )}
    </ConfirmModal>
  );
}
